import React, {useEffect, useReducer, useRef} from 'react';
import {v4 as uuid} from 'uuid';

// Modules
import config from 'src/config/config.json';

// Components
import Previewer from '@deezer/previewer';
import DeezerUrlMediaProvider, {
	Token,
	PreviewRequest,
} from '@deezer/deezer-media-url-provider';

// Utils
import {createContext} from '@deezer/react-utils';

// Types
import {
	GetTokensDocument,
	type GetTokensQuery,
	type GetTokensQueryVariables,
	type TrackMediaToken,
} from '@graphql/generated';
import type {Media, WithToken} from '@deezer/deezer-media-url-provider';
import {useGraphQLClient} from '@deezer/graphql-client/react';
import {useURLParams} from './URLParamsProvider';
import {MobileOS} from './types';

export type StreamPayload = {
	id?: string;
	startTime?: number;
	startPlayTime?: number;
	endTime?: number;
	listeningDuration?: number;
	url?: string;
};

type PreviewProviderState = {
	dispatch: (action: PreviewerHistoryReducerActions) => void;
	playTrack: (
		trackToken: TrackMediaToken,
		streamId?: string,
		trackId?: string,
		onStart?: () => void,
		onError?: () => void,
	) => Promise<{stop: () => void}>;
	tracks: Record<string, StreamPayload>;
};

const [PreviewContextProvider, usePreviewerContext, PreviewContext] =
	createContext<PreviewProviderState>({
		errorMessage:
			' You tried to access a feature requiring a PreviewContext. Did you forget to wrap your component in a PreviewContextProvider?',
		name: 'PreviewContextProvider',
		strict: false,
	});

type PreviewProviderProps = {
	children?: React.ReactNode;
};

async function* getUrlFromToken(
	urlMediaProvider: DeezerUrlMediaProvider<WithToken>,
	token: Token,
) {
	const track = {token};
	const res = await urlMediaProvider.getURLs([track], new PreviewRequest());

	const media = res.get(track);

	if (media instanceof MediaError || !media) {
		throw new Error("Couldn't find mp3");
	}

	for await (const {url} of (media as Media[])[0].sources) {
		const isItAGoodUrl = await fetch(url, {method: 'HEAD'})
			.then((response) => {
				return response.ok;
			})
			.catch(() => {
				return false;
			});

		if (isItAGoodUrl) {
			yield url;
		}
	}

	// We couldn't find good urls
	yield undefined;
}

export type PreviewerHistoryState = Record<string, StreamPayload>;

const previewerHistoryInitialState: PreviewerHistoryState = {};

export enum PreviewerHistoryActions {
	ADD_PLAY = 'ADD_PLAY',
	CLEAR_HISTORY = 'CLEAR_HISTORY',
	SET_START_TIME = 'SET_START_TIME',
	SET_START_PLAY_TIME = 'SET_START_PLAY_TIME',
	SET_END_TIME = 'SET_END_TIME',
	SET_LISTENING_DURATION = 'SET_LISTENING_DURATION',
	SET_TRACK_ID = 'SET_TRACK_ID',
}

export type PreviewerHistoryReducerActions =
	| {
			type: PreviewerHistoryActions.ADD_PLAY;
			payload: {streamId: string; trackId?: string};
	  }
	| {
			type: PreviewerHistoryActions.CLEAR_HISTORY;
			payload: {streamId: string};
	  }
	| {
			type: PreviewerHistoryActions.SET_START_TIME;
			payload: {streamId: string; startTime: number; url: string};
	  }
	| {
			type: PreviewerHistoryActions.SET_START_PLAY_TIME;
			payload: {streamId: string; startPlayTime: number};
	  }
	| {
			type: PreviewerHistoryActions.SET_LISTENING_DURATION;
			payload: {streamId: string; listeningDuration: number};
	  }
	| {
			type: PreviewerHistoryActions.SET_TRACK_ID;
			payload: {streamId: string; trackId: string};
	  };

export const previewerHistoryReducer = (
	state = previewerHistoryInitialState,
	action: PreviewerHistoryReducerActions,
): PreviewerHistoryState => {
	switch (action.type) {
		case PreviewerHistoryActions.ADD_PLAY: {
			return {
				...state,
				[action.payload.streamId]: action.payload.trackId
					? {
							id: action.payload.trackId,
						}
					: {},
			};
		}
		case PreviewerHistoryActions.SET_TRACK_ID: {
			return {
				...state,
				[action.payload.streamId]: {
					...state[action.payload.streamId],
					id: action.payload.trackId,
				},
			};
		}
		case PreviewerHistoryActions.SET_START_TIME: {
			return {
				...state,
				[action.payload.streamId]: {
					...state[action.payload.streamId],
					startTime: action.payload.startTime,
					url: action.payload.url,
				},
			};
		}
		case PreviewerHistoryActions.SET_START_PLAY_TIME: {
			return {
				...state,
				[action.payload.streamId]: {
					...state[action.payload.streamId],
					startPlayTime: action.payload.startPlayTime,
				},
			};
		}
		case PreviewerHistoryActions.SET_LISTENING_DURATION: {
			return {
				...state,
				[action.payload.streamId]: {
					...state[action.payload.streamId],
					listeningDuration: action.payload.listeningDuration,
				},
			};
		}
		case PreviewerHistoryActions.CLEAR_HISTORY: {
			delete state[action.payload.streamId];

			return state;
		}
		default: {
			return state;
		}
	}
};

const PreviewProvider: React.FC<PreviewProviderProps> = ({children}) => {
	const previewer = useRef<Previewer | null>(null);
	const gqlClient = useGraphQLClient();
	const {platform} = useURLParams();

	useEffect(() => {
		previewer.current = new Previewer({
			autoUnlock: platform === MobileOS.NULL,
			autoplay: true,
			volume: 0.5,
		});
	}, []);

	const urlMediaProviderPromise = React.useMemo(async () => {
		if (!gqlClient) {
			throw new Error('Missing gql client');
		}

		const licenseResult = await gqlClient.query<
			GetTokensQuery,
			GetTokensQueryVariables
		>({
			query: GetTokensDocument,
		});
		const license = licenseResult.data.tokens.mediaServiceLicenseToken;
		if (!license || !license.token) {
			return null;
		}

		return new DeezerUrlMediaProvider(
			config.url_media_service,
			new Token(license.token, {expire: license.expirationDate}),
		);
	}, [gqlClient]);

	const [previewHistory, dispatch] = useReducer(
		previewerHistoryReducer,
		previewerHistoryInitialState,
	);

	const playTrack = React.useCallback(
		async (
			trackMediaToken: TrackMediaToken,
			streamId?: string,
			trackId?: string,
			onStart: () => void = () => void 0,
			onError: () => void = () => void 0,
		): Promise<{stop: () => void}> => {
			if (!previewer.current) {
				return {
					stop: () => {},
				};
			}
			const currentStreamId = streamId || uuid();

			dispatch({
				type: PreviewerHistoryActions.ADD_PLAY,
				payload: {streamId: currentStreamId, trackId: trackId || undefined},
			});

			const urlMediaProvider = await urlMediaProviderPromise;

			if (!urlMediaProvider) {
				throw new Error('Url Media Provider is not ready');
			}

			const token = new Token(trackMediaToken.payload);

			const start = () => {
				if (!previewer.current) return;
				dispatch({
					type: PreviewerHistoryActions.SET_START_TIME,
					payload: {
						streamId: currentStreamId,
						startTime: Date.now(),

						url: previewer.current.url,
					},
				});
			};

			const stop = () => {
				if (!previewer.current) return;
				dispatch({
					type: PreviewerHistoryActions.SET_LISTENING_DURATION,
					payload: {
						streamId: currentStreamId,
						listeningDuration: previewer.current.currentTime,
					},
				});
			};

			const onPlay = () => {
				onStart();
				start();
			};

			const onEnded = () => {
				stop();
			};

			if (previewer.current.locked) {
				// eslint-disable-next-line no-console
				console.warn('Player has not been unlocked, things will break');
			}

			for await (const url of getUrlFromToken(urlMediaProvider, token)) {
				// No url given means we tried all url given to us but none worked
				if (!url) {
					return {
						stop: () => {},
					};
				}

				previewer.current.addEventListener('error', onError, {once: true});
				previewer.current.addEventListener('playing', onPlay, {once: true});
				previewer.current.addEventListener('ended', onEnded, {once: true});

				previewer.current.load(url);
				try {
					await previewer.current.play();
					// Successfull play means we can exit this loop
					break;
				} catch (error) {
					// If we cannot play, try the next url
					continue;
				}
			}

			return {
				stop: () => {
					if (!previewer.current) return;
					previewer.current.removeEventListener('error', onError);
					previewer.current.removeEventListener('playing', onPlay);
					previewer.current.removeEventListener('ended', onEnded);
					stop();
					previewer.current.stop();
				},
			};
		},
		[urlMediaProviderPromise],
	);

	return (
		<PreviewContextProvider
			value={{playTrack, tracks: previewHistory, dispatch}}
		>
			{children}
		</PreviewContextProvider>
	);
};

export default PreviewProvider;

export {usePreviewerContext, PreviewContextProvider, PreviewContext};
